import React from "react";

const PublicSafety = () => {
  return (
    <div style={styles.container}>
      <div style={styles.grid}>

        {/* ================= LEFT COLUMN ================= */}
        <div style={styles.leftCol}>

          <div style={styles.pageHeader}>
            <h1 style={styles.pageTitle}>Public Safety & Government</h1>
            <p style={styles.pageSubtitle}>
              AI-Assisted Public Safety Intelligence for Schools,
              Communities, NGOs, Researchers and Public Sector Organizations
            </p>
          </div>

          {/* K-12 Priority Callout */}
          <div style={styles.schoolCallout}>
            <span style={styles.schoolIcon}>🏫</span>
            <div>
              <strong style={{ color: "#fbbf24" }}>Priority Focus: K-12 School Safety</strong>
              <p style={styles.schoolCalloutText}>
                School districts can fund FentaSafe deployment through the COPS Office
                School Violence Prevention Program (SVPP) — up to $500,000 per district
                for evidence-based safety technology. FentaSafe is aligned with
                SchoolSafety.gov threat assessment and substance use risk detection frameworks.
              </p>
            </div>
          </div>

          {/* SVPP Funding Pathway — moved up for school administrator visibility */}
          <div style={styles.svppBox}>
            <h4 style={styles.svppTitle}>💰 SVPP Funding Pathway for School Districts</h4>
            <p style={styles.svppText}>
              The U.S. Department of Justice COPS Office confirmed at the 2026 SVPP
              webinar that <em>"real-time situational awareness, risk assessment, and
              emergency alerting for school staff may be considered allowable under SVPP."</em>
            </p>
            <p style={styles.svppText}>
              School districts applying for SVPP grants (up to $500,000 per district,
              deadline: Grants.gov August 4 / JustGrants August 11, 2026) can include
              FentaSafe as an eligible technology deployment cost.
            </p>
            <p style={{ ...styles.svppText, marginBottom: 0 }}>
              <strong style={{ color: "#fbbf24" }}>For school districts:</strong>{" "}
              Contact us to discuss how FentaSafe aligns with your SVPP application.
            </p>
          </div>

          <div>
            <h2>FentaSafe AI-Driven Public Safety Intelligence Platform</h2>

            <p style={styles.highlight}>
              Designed to support early threat detection, situational awareness, and proactive
              intervention across schools, public environments, and high-risk civilian infrastructure.
            </p>

            <p>
              FentaSafe is an integrated safety intelligence platform focused on identifying
              emerging risks such as <strong>fentanyl exposure, environmental hazards, and
              evolving threat patterns</strong> before incidents escalate.
            </p>

            <p>
              The platform enables public safety organizations to move from reactive response to
              <strong> proactive, intelligence-driven operations</strong>.
            </p>

            <hr style={styles.hr} />

            {/* CHALLENGE */}
            <h3>The Public Safety Challenge</h3>

            <p>
              Modern threats often emerge from weak signals — environmental anomalies, concealed
              risks, and contextual indicators that are difficult to detect using conventional
              systems.
            </p>

            <p style={styles.challengeHighlight}>
              There is a critical need for systems that provide early detection, predictive
              insight, and coordinated response support.
            </p>

            {/* Public Safety Alignment */}
            <h3>Public Safety Alignment</h3>

            <p>
              FentaSafe aligns with evolving public safety priorities including early risk
              detection, threat assessment, situational awareness, and AI-assisted decision
              support — reflecting key frameworks from SchoolSafety.gov, COPS SVPP program
              guidelines, and federal school safety technology initiatives.
            </p>

            <p>
              The platform supports use cases relevant to federal, state, and local safety
              initiatives, including school safety, community protection, and critical
              infrastructure monitoring.
            </p>


          </div>
        </div>

        {/* ================= RIGHT COLUMN ================= */}
        <div style={styles.rightCol}>
          <h2>Public Safety Governance Center</h2>

          <p>
            FentaSafe is designed to enhance safety while preserving privacy and civil liberties.
          </p>

          <h3>Privacy & Civil Liberties</h3>
          <ul>
            <li>No facial recognition</li>
            <li>No individual tracking</li>
            <li>Zone-level insights only</li>
          </ul>

          <h3>Ethical System Design</h3>
          <ul>
            <li>Human-in-the-loop decision support</li>
            <li>No automated enforcement actions</li>
            <li>Explainable and transparent alerts</li>
          </ul>

          <h3>Security & Access</h3>
          <ul>
            <li>Role-based access control (RBAC)</li>
            <li>Multi-factor authentication (MFA)</li>
            <li>Government IAM compatibility</li>
          </ul>

          <h3>Regulatory Alignment</h3>
          <ul>
            <li>Supports controlled pilot deployments</li>
            <li>Aligned with public sector procurement models</li>
            <li>Governance-first implementation approach</li>
            <li>SVPP-eligible technology deployment</li>
          </ul>

          <p style={styles.disclaimer}>
            Information provided for evaluation, research, and pilot discussions only.
          </p>
        </div>
      </div>

      {/* ================= CAPABILITY GRID ================= */}
      <div style={styles.capabilityGrid}>

        <div style={styles.capCard}>
          <h3>Core Capabilities</h3>
          <ul>
            <li>Fentanyl and substance-related early warning signals</li>
            <li>Environmental and contextual threat detection</li>
            <li>Distributed multi-zone monitoring</li>
            <li>Real-time alerting and escalation awareness</li>
          </ul>
        </div>

        <div style={styles.capCard}>
          <h3>Operational Architecture</h3>
          <ul>
            <li>Multi-signal ingestion and fusion</li>
            <li>AI-driven risk scoring and anomaly detection</li>
            <li>Supervisor-guided orchestration and intervention</li>
            <li>Federated intelligence across facilities and regions</li>
          </ul>
        </div>

        <div style={styles.capCard}>
          <h3>AI-Assisted Safety Operations</h3>
          <ul>
            <li>Predictive escalation forecasting</li>
            <li>Adaptive learning from operator decisions</li>
            <li>Explainable AI-driven alerts</li>
            <li>Cross-site network intelligence</li>
          </ul>
        </div>

        <div style={styles.capCard}>
          <h3>Deployment Readiness</h3>
          <p>
            Technology Readiness Level (TRL): <strong>6–7</strong> — suitable
            for controlled pilot environments.
          </p>
          <ul>
            <li>Modular IoT and sensor integration</li>
            <li>Cloud-native scalable architecture</li>
            <li>No disruptive infrastructure changes required</li>
            <li>Phased deployment and pilot programs supported</li>
          </ul>
        </div>

      </div>

      {/* EXECUTIVE SUMMARY */}
      <div style={styles.summaryBox}>
        <h3 style={styles.summaryTitle}>Executive Public Safety Summary</h3>
        <p style={styles.summaryText}>
          FentaSafe enables proactive public safety operations through AI-assisted threat
          detection, environmental intelligence, explainable decision support, and
          human-governed risk management. The platform supports schools, communities,
          NGOs, and public sector organizations in identifying emerging risks and
          coordinating timely interventions while preserving privacy and civil liberties.
        </p>
        <p style={{ ...styles.summaryText, marginTop: 12 }}>
          School districts can fund FentaSafe deployment through the COPS Office School
          Violence Prevention Program (SVPP). NSF SBIR Phase I funding supports the
          continued R&D validation of the platform's AI risk intelligence architecture.
        </p>
      </div>

      {/* CONTACT */}
      <div style={styles.contactBox}>
        <h3 style={styles.summaryTitle}>Contact FentaSafe</h3>
        <div style={styles.contactContent}>
          <div><strong>Premarajan Abel</strong></div>
          <div>Founder & Lead Architect, FentaSafe Technologies, Inc.</div>
          <div style={{ marginTop: 8 }}>
            <a href="mailto:premarajan@fentasafe.org" style={styles.emailLink}>
              premarajan@fentasafe.org
            </a>
          </div>
          <div style={{ marginTop: 4 }}>
            <a href="https://www.fentasafe.org" style={styles.emailLink}>
              www.fentasafe.org
            </a>
          </div>
          <div style={{ marginTop: 8, color: "#64748b", fontSize: "0.85rem" }}>
            Available for pilot evaluations, research collaborations,
            and SVPP-aligned school district discussions.
          </div>
        </div>
      </div>

    </div>
  );
};

export default PublicSafety;

const styles = {
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "40px 24px",
    color: "#fff",
    background: "#0f172a",
    lineHeight: 1.7,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1.5fr 1fr",
    gap: "30px",
    alignItems: "start",
  },

  leftCol: {},

  rightCol: {
    background: "#020617",
    border: "1px solid #1e293b",
    borderRadius: 14,
    padding: 24,
    position: "sticky",
    top: 90,
    height: "fit-content",
  },

  pageHeader: {
    textAlign: "center",
    marginBottom: 28,
  },

  pageTitle: {
    fontSize: 42,
    marginBottom: 15,
  },

  pageSubtitle: {
    color: "#94a3b8",
    fontSize: 18,
    maxWidth: 900,
    margin: "0 auto",
  },

  schoolCallout: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    background: "rgba(251, 191, 36, 0.08)",
    border: "1px solid rgba(251, 191, 36, 0.3)",
    borderRadius: 10,
    padding: "16px 18px",
    marginBottom: 24,
  },

  schoolIcon: {
    fontSize: "1.8rem",
    lineHeight: 1,
    marginTop: 2,
  },

  schoolCalloutText: {
    margin: "6px 0 0",
    fontSize: "0.9rem",
    color: "#94a3b8",
    lineHeight: 1.6,
  },

  highlight: {
    marginBottom: 16,
    color: "#93c5fd",
    fontWeight: 500,
  },

  challengeHighlight: {
    marginTop: 16,
    fontWeight: 600,
    color: "#e2e8f0",
  },

  hr: {
    border: "none",
    borderTop: "1px solid #1e293b",
    margin: "32px 0",
  },

  svppBox: {
    marginTop: 24,
    padding: "18px 20px",
    background: "rgba(37, 99, 235, 0.08)",
    border: "1px solid rgba(37, 99, 235, 0.25)",
    borderRadius: 10,
  },

  svppTitle: {
    color: "#93c5fd",
    marginBottom: 10,
    fontSize: "1rem",
    fontWeight: 700,
  },

  svppText: {
    color: "#94a3b8",
    fontSize: "0.88rem",
    lineHeight: 1.7,
    marginBottom: 10,
  },

  disclaimer: {
    fontSize: 13,
    opacity: 0.7,
    fontStyle: "italic",
    marginTop: 16,
  },

  capabilityGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: 20,
    marginTop: 28,
  },

  capCard: {
    background: "#111827",
    padding: 20,
    borderRadius: 14,
    border: "1px solid #1e293b",
  },

  summaryBox: {
    marginTop: 24,
    padding: 28,
    background: "#111827",
    borderRadius: 14,
    border: "1px solid #1e293b",
    textAlign: "center",
  },

  summaryTitle: {
    textAlign: "center",
    marginBottom: 16,
  },

  summaryText: {
    textAlign: "center",
    color: "#cbd5e1",
    lineHeight: 1.8,
    maxWidth: 900,
    margin: "0 auto",
  },

  contactBox: {
    marginTop: 20,
    padding: 28,
    background: "#111827",
    borderRadius: 14,
    border: "1px solid #1e293b",
    textAlign: "center",
  },

  contactContent: {
    marginTop: 12,
    lineHeight: 1.8,
    color: "#cbd5e1",
  },

  emailLink: {
    color: "#60a5fa",
    textDecoration: "none",
    fontWeight: 500,
  },
};
